import { Component, ElementRef, OnInit } from '@angular/core';

import { PasskeyLoginResponse } from 'PasskeysApp/interfaces/passkeys.interface';

import { PasskeyLoginErrorService } from './passkey-login-error.service';
import { CookieService } from 'ngx-cookie-service';
import { PasskeysService } from 'PasskeysApp/services/passkeys.service';

const PASSKEY_LOGIN_HINT_COOKIE = 'passkey_login_hint';
const CREDENTIAL_UNKNOWN_MESSAGE = 'This passkey is no longer valid. Please sign in with your password.';
const EMAIL_MISMATCH_MESSAGE =
    "We couldn't link your social account because it's associated with a " +
    'different email than the one you signed in with.';

@Component({ standalone: false, selector: 'ycn-passkey-login', templateUrl: './passkey-login.template.html' })
export class PasskeyLoginComponent implements OnInit {
    showFallbackLink: boolean = false;

    constructor(
        private passkeysService: PasskeysService,
        private cookieService: CookieService,
        private passkeyLoginErrorService: PasskeyLoginErrorService,
        private eltRef: ElementRef
    ) {}

    ngOnInit(): void {
        // If we're in manual mode and this event is triggered, it means we're in the account login wizard at the
        // social login linking step, and we should avoid prompting the component to automatically show up or autofill
        this.eltRef.nativeElement.addEventListener('ycn-trigger-passkey-login', () => this.login());
        if (this.eltRef.nativeElement.hasAttribute('data-manual')) {
            return;
        }

        // The hint cookie is set server-side after a successful passkey registration or login.
        // Its presence tells us this browser has used a passkey on this site before, so we can
        // auto-trigger the modal flow. Conditional UI still runs as a fallback if the cookie
        // is missing (e.g. browser data was cleared).
        if (this.cookieService.check(PASSKEY_LOGIN_HINT_COOKIE)) {
            this.showFallbackLink = true;
            this.login();
            return;
        }

        if (typeof window.PublicKeyCredential?.isConditionalMediationAvailable !== 'function') {
            return;
        }
        PublicKeyCredential.isConditionalMediationAvailable().then((available) => {
            if (available) {
                this.loginConditional();
            }
        });
    }

    login(): void {
        this.passkeysService.authenticatePasskey().subscribe({
            next: this.handleLoginSuccess,
            error: this.handleLoginError,
        });
    }

    loginConditional(): void {
        this.passkeysService.authenticatePasskeyConditional().subscribe({
            next: this.handleLoginSuccess,
            // The conditional request runs passively in the background, so most errors
            // (e.g. NotAllowedError when no passkey is available) should not be surfaced.
            // The exception is credential_unknown: the user actively picked a passkey from
            // autofill and the server rejected it, so we need to tell them why nothing happened.
            error: (error: { status?: number; error?: { error?: string } }) => {
                if (error.status === 403 && error.error?.error === 'credential_unknown') {
                    this.passkeyLoginErrorService.setError(CREDENTIAL_UNKNOWN_MESSAGE);
                }
            },
        });
    }

    private handleLoginSuccess = (response: PasskeyLoginResponse): void => {
        // URL comes in the response only when the login is successful, and it was already validated by the backend.
        window.location.href = response.url;
    };

    private handleLoginError = (error: { name?: string; status?: number; error?: { error?: string } }): void => {
        if (error.name === 'NotAllowedError') {
            // User cancelled the login prompt.
            return;
        }

        if (error.status === 403 && error.error?.error === 'credential_unknown') {
            // Server has no record of this credential — likely deleted via admin.
            // Cookie was cleared by the server and the credential was signaled to the
            // authenticator, so this is the only prompt the user will see.
            this.showFallbackLink = false;
            this.passkeyLoginErrorService.setError(CREDENTIAL_UNKNOWN_MESSAGE);
        } else if (error.status === 403 && error.error?.error === 'email_mismatch') {
            this.passkeyLoginErrorService.setError(EMAIL_MISMATCH_MESSAGE);
        } else if (error.status === 403) {
            this.passkeyLoginErrorService.setError('Invalid Passkey.');
        } else {
            this.passkeyLoginErrorService.setError('Server is unable to process the provided key.');
        }
    };
}
